// System
import React, { useEffect, useMemo } from 'react';

// Components
import { TablePagination } from '@mui/material';
import { TableBodyEx, TableContainerEx, TableEx } from '@Components/shared/tables/components';
import {
  ColumnVisibilityPresenter,
  TableHeadColumnsPresenter,
  TableHeadPresenter,
  TableRowPresenter,
} from '@Components/shared/tables/presenters';
import { LoadingScreen } from '@Components/shared/loaders/LoadingScreen';

// Utils
import { applyColumnVisibility } from '@utils/data/applyColumnVisibility';
import { formatPaginationTotal } from '@utils/data/number/localeNumberFormat';

//  Table
import { useProviderIgnoredRequestsTableColumns } from './useProviderIgnoredRequestsTableColumns';

// Redux
import { useDispatch } from 'react-redux';
import { ProviderIgnoredRequestsItemDto } from '@contracts/api/dto/ProviderIgnoredRequests/ProviderIgnoredRequestsDto';
import { usePaginatedProviderIgnoredRequestsQuery } from '@Src/generated.graphql';
import { useProviderIgnoredRequestsTable } from '@Modal/hooks/providerIgnoredRequests/useProviderIgnoredRequestsTable';
import { useAuthorization } from '@Modal/hooks/authorization/useAuthorization';

export const ProviderIgnoredRequestsTable: React.FC = () => {
  const dispatch = useDispatch();
  const {
    page,
    limit,
    total,
    setLimit,
    setPage,
    setTotal,
    columnVisibility,
    visiblityCustomizable,
    providerIgnoredRequestsListFilters,
    providerIgnoredRequestsTableFilters,
  } = useProviderIgnoredRequestsTable();
  const { userPermissions } = useAuthorization();
  const columns = useProviderIgnoredRequestsTableColumns();

  const permissionToViewProviderIgnoredRequests = userPermissions?.includes(
    'CAN_VIEW_PROVIDER_IGNORED_REQUESTS'
  );

  const { data, loading, refetch } = usePaginatedProviderIgnoredRequestsQuery({
    variables: {
      limit,
      page,
      filter: providerIgnoredRequestsTableFilters,
    },
    skip: !permissionToViewProviderIgnoredRequests,
  });

  const visibleColumns = useMemo(
    () => applyColumnVisibility(columns, providerIgnoredRequestsListFilters),
    [columns, columnVisibility]
  );

  useEffect(() => {
    if (data?.paginatedProviderIgnoredRequests) {
      const { total, limit, page } = data.paginatedProviderIgnoredRequests;
      dispatch(setTotal(total));
      dispatch(setLimit(limit));
      dispatch(setPage(page));
    }
  }, [data, dispatch]);

  const header = useMemo(
    () => (
      <TableHeadPresenter>
        <TableHeadColumnsPresenter columns={visibleColumns} />
        {/* @ts-ignore */}
        <ColumnVisibilityPresenter columns={columns} content={visiblityCustomizable} />
      </TableHeadPresenter>
    ),
    [columns]
  );

  if (!permissionToViewProviderIgnoredRequests) return <h1>No Permission</h1>;
  if (loading) return <LoadingScreen />;

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    refetch();
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setLimit(parseInt(event.target.value, 10)));
    dispatch(setPage(1));
    refetch();
  };

  if (
    data?.paginatedProviderIgnoredRequests?.docs?.length === 0 ||
    !data?.paginatedProviderIgnoredRequests?.docs
  ) {
    return <h1>No Data</h1>;
  }

  return (
    <>
      <TableContainerEx sx={{ height: '70vh' }}>
        <TableEx name="ProviderTranasctionsList">
          {header}
          <TableBodyEx>
            {/* @ts-ignore */}
            {data?.paginatedProviderIgnoredRequests?.docs?.map(
              // @ts-ignore
              (row: ProviderIgnoredRequestsItemDto) => (
                <TableRowPresenter
                  id={row.betId}
                  key={row.betId}
                  columns={visibleColumns}
                  row={row}
                  onClick={() => {}}
                />
              )
            )}
          </TableBodyEx>
        </TableEx>
      </TableContainerEx>
      <TableEx name="ProviderIgnoredRequestsListFooter">
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${formatPaginationTotal(count)}`
          }
        />
      </TableEx>
    </>
  );
};
